import { TasksByEntityDTO } from './Task.Model';
import { EntityDeactivationValidation } from './Task.Model';
import { Injectable } from '@angular/core';
import { TaskRepository } from '../Core-Repositories';
import { lastValueFrom } from 'rxjs';
import { TaskModalsService } from './taskModals.service';

import * as _ from 'lodash';

export interface ReassignTaskRequestModel extends Core.UserTeamModel {
    taskIds: number[];
    searchTimestamp: Date;
}

export interface ReassignUserTaskRequestModel {
    taskIds: number[];
    searchTimestamp: Date;
    teamId: number;
    userId: System.Guid;
}

@Injectable({ providedIn: 'root' })
export class TaskService {
    constructor(
        private readonly _taskRepository: TaskRepository,
        private readonly _taskModalsService: TaskModalsService,
    ) {}

    rescindTask(task): Promise<any> {
        return lastValueFrom(this._taskRepository.rescindTask(task));
    }

    insertTask(task, newTaskTypeID: number, actionID: number): Promise<any> {
        return lastValueFrom(this._taskRepository.insertTask(task, actionID, newTaskTypeID));
    }

    updateTask(task): Promise<any> {
        return lastValueFrom(this._taskRepository.updateTask(task));
    }

    deleteTask(task): Promise<any> {
        return lastValueFrom(this._taskRepository.deleteTask(task));
    }

    skipTask(task): Promise<any> {
        return lastValueFrom(this._taskRepository.skipTask(task));
    }

    async completeTask(task, allowAddYearPrompt: boolean = false): Promise<any> {
        return this._taskRepository.completeTask(task, allowAddYearPrompt);
    }

    checkIfEntityMayBeDeactivated(entityID: number, entityTypeID: number): Promise<EntityDeactivationValidation> {
        return lastValueFrom(this._taskRepository.checkIfEntityMayBeDeactivated(entityID, entityTypeID));
    }

    getByEntity(entityID: number, entityTypeID: number): Promise<TasksByEntityDTO> {
        return lastValueFrom(this._taskRepository.getByEntity(entityID, entityTypeID));
    }

    getTaskSummaryByEntity(entityID: number, entityTypeID: number): Promise<any> {
        return lastValueFrom(this._taskRepository.getTaskSummaryByEntity(entityID, entityTypeID));
    }

    getInsertOptions(entityID: number, entityTypeID: number, taskID: number): Promise<any> {
        return lastValueFrom(this._taskRepository.getInsertOptions(entityID, entityTypeID, taskID));
    }

    changeContacts(changeRequest: ReassignUserTaskRequestModel): Promise<void> {
        return lastValueFrom(this._taskRepository.changeContacts(changeRequest));
    }

    reassignMany(reassignRequest: ReassignTaskRequestModel): Promise<void> {
        return lastValueFrom(this._taskRepository.reassignMany(reassignRequest));
    }

    skipMany(taskIDs: number[]): Promise<void> {
        return lastValueFrom(this._taskRepository.skipMany(taskIDs));
    }

    completeMany(taskIDs: number[]): Promise<void> {
        return lastValueFrom(this._taskRepository.completeMany(taskIDs));
    }

    validateAssessorStateReqs(taskSeriesIds: number[]): Promise<any> {
        return lastValueFrom(this._taskRepository.validateAssessorStateReqs(taskSeriesIds));
    }


    // Errors and warnings are string arrays, error message will be shown over error messages
    // and warning message will be shown over warning messages (warningMessage is optional,
    // even if the warning array has entries)
    showErrorNotificationModal(
        errors: string[],
        warnings: string[],
        errorMessage: string,
        warningMessage?: string,
    ): void {
        this._taskModalsService.showErrorNotificationModal(errors, warnings, errorMessage, warningMessage);
    }

    getMaxTasksWide(taskGroupsBySequence: any[]): number {
        return _.chain(taskGroupsBySequence)
            .map((taskGroupCollection) => {
                return taskGroupCollection.taskGroups.length;
            })
            .max()
            .value();
    }

    splitIntoGroups(tasks) {
        let idSequencer = 0;
        const taskGroupArr = [];
        const firstGroup = {
            tasks: [],
            sequence: 1,
            id: idSequencer++,
        };
        const firstTask = _.find(tasks, { sequence: 1 });

        const compileThing = (task, group) => {
            group.tasks.push(task);

            if (task.pointsTo.length > 1) {
                _.forEach(task.pointsTo, (taskID) => {
                    const firstTask = _.find(tasks, { taskID: taskID });
                    const newGroup = {
                        tasks: [],
                        sequence: group.sequence + 1,
                        id: idSequencer++,
                    };
                    compileThing(firstTask, newGroup);
                });

                taskGroupArr.unshift(group);
            } else if (task.pointsTo.length === 1) {
                const nextTask = _.find(tasks, { taskID: task.pointsTo[0] });

                const otherTasksThatPointToIt = _.filter(tasks, (t) => {
                    return _.includes(t.pointsTo, task.pointsTo[0]) && t.taskID !== task.taskID;
                });

                const taskIds = _.chain(taskGroupArr).map('tasks').flatten().map('taskID').value();

                if (otherTasksThatPointToIt.length) {
                    taskGroupArr.unshift(group);
                    if (!_.includes(taskIds, nextTask.taskID)) {
                        const newGroup = {
                            tasks: [],
                            sequence: group.sequence + 1,
                            id: idSequencer++,
                        };
                        compileThing(nextTask, newGroup);
                    }
                } else {
                    compileThing(nextTask, group);
                }
            } else {
                taskGroupArr.unshift(group);
            }
        };

        compileThing(firstTask, firstGroup);

        return _.chain(taskGroupArr)
            .groupBy('sequence')
            .map((taskGroups, sequence) => {
                return {
                    taskGroups: taskGroups,
                    sequence: Number(sequence),
                };
            })
            .value();
    }
}
